import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PhotoIcon, FolderOpenIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import ExperimentLayout from '../../components/experiments/ExperimentLayout';
import GalleryScene from '../../components/gallery/GalleryScene';
import { LocalFilesSource, isSupportedImage } from '../../lib/photo-sources/local';
import { GooglePhotosPickerSource } from '../../lib/photo-sources/googlePhotos';
import { filesFromDataTransfer } from '../../lib/photo-sources/dropFiles';
import type { PhotoItem, PhotoSource, PhotoSourceProgress } from '../../lib/photo-sources/types';

type ConnectState =
  | { status: 'idle' }
  | { status: 'loading'; progress: PhotoSourceProgress }
  | { status: 'ready'; items: PhotoItem[]; ephemeral: boolean }
  | { status: 'error'; message: string; hint?: string };

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/** Google returns this when the account isn't on the Testing-mode tester list — which looks
 *  identical to a cancel unless we say otherwise. */
function describeAuthFailure(message: string): { message: string; hint?: string } {
  if (/access_denied/i.test(message)) {
    return {
      message: 'Google blocked the sign-in.',
      hint: "This app is in testing mode, so only accounts I've added by hand can connect. If you think you should have access, that's on me — not something you can fix from your side.",
    };
  }
  if (/popup|closed|blocked/i.test(message)) {
    return {
      message: 'The Google sign-in window closed before finishing.',
      hint: 'If your browser blocked the popup, allow popups for this site and try again.',
    };
  }
  return { message };
}

const PhotoGalleryExperiment: React.FC = () => {
  const [state, setState] = useState<ConnectState>({ status: 'idle' });
  const [dragging, setDragging] = useState(false);
  const [pickerUri, setPickerUri] = useState<string | null>(null);
  const sourceRef = useRef<PhotoSource | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Drag events fire for every child element; count enter/leave to know when we've truly left.
  const dragDepth = useRef(0);

  const teardown = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    sourceRef.current?.dispose();
    sourceRef.current = null;
    setPickerUri(null);
  }, []);

  useEffect(() => teardown, [teardown]);

  const run = useCallback(
    async (source: PhotoSource, initialLabel: string) => {
      teardown();

      const controller = new AbortController();
      abortRef.current = controller;
      sourceRef.current = source;

      setState({ status: 'loading', progress: { loaded: 0, total: 0, label: initialLabel } });

      try {
        await source.connect(controller.signal, (progress) => {
          if (!controller.signal.aborted) setState({ status: 'loading', progress });
        });
        if (controller.signal.aborted) return;

        const items = source.list();
        if (items.length === 0) throw new Error('No photos came back.');
        setState({ status: 'ready', items, ephemeral: source.ephemeral });
      } catch (error) {
        if (controller.signal.aborted) return;
        source.dispose();
        sourceRef.current = null;
        const raw = error instanceof Error ? error.message : String(error);
        setState({ status: 'error', ...describeAuthFailure(raw) });
      } finally {
        setPickerUri(null);
      }
    },
    [teardown]
  );

  const loadLocal = useCallback(
    (files: File[]) => {
      const images = files.filter(isSupportedImage);
      if (images.length === 0) {
        setState({
          status: 'error',
          message: 'No supported images in that drop.',
          hint: 'Try JPEG, PNG, WebP, GIF, or AVIF.',
        });
        return;
      }
      void run(new LocalFilesSource(images), 'Reading photos');
    },
    [run]
  );

  const connectGoogle = useCallback(() => {
    if (!clientId) return;
    void run(
      new GooglePhotosPickerSource({ clientId, onPickerReady: setPickerUri }),
      'Waiting for Google authorization'
    );
  }, [run]);

  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      loadLocal(await filesFromDataTransfer(event.dataTransfer));
    },
    [loadLocal]
  );

  const reset = useCallback(() => {
    teardown();
    setState({ status: 'idle' });
  }, [teardown]);

  if (state.status === 'ready') {
    // Deliberately *not* wrapped in ExperimentLayout: its `max-w-6xl` is right for prose and
    // wrong for a gallery, and it left a third of a wide screen empty. This gets its own
    // container, still capped so rows don't stretch absurdly on an ultrawide display.
    return (
      <section className="mx-auto w-full max-w-[1800px] space-y-4 px-3 sm:px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-white/45">
            {state.items.length} photos ·{' '}
            {state.ephemeral
              ? 'loaded from Google Photos, this session lasts about an hour'
              : 'read from your device, nothing was uploaded'}
          </p>
          <button type="button" onClick={reset} className="btn-outline text-xs">
            Choose different photos
          </button>
        </div>
        <GalleryScene items={state.items} />
      </section>
    );
  }

  const loading = state.status === 'loading';

  return (
    <ExperimentLayout
      title="Photo Gallery"
      summary="Connect a photo source, then scroll through your own photos. Built source-agnostic: local files and Google Photos feed the same gallery."
      meta={[
        <span key="motion" className="px-2 py-1 rounded-md bg-brand-500/10 text-brand-200">
          framer-motion
        </span>,
        <span key="google" className="px-2 py-1 rounded-md bg-brand-400/10 text-brand-100">
          picker api
        </span>,
        <span key="phase" className="px-2 py-1 rounded-md bg-brand-300/10 text-brand-100">
          phase 4 of 7
        </span>,
      ]}
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {/* Local files */}
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              dragDepth.current += 1;
              setDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              dragDepth.current -= 1;
              if (dragDepth.current <= 0) setDragging(false);
            }}
            onDrop={onDrop}
            className={[
              'flex flex-col items-center rounded-xl border border-dashed p-8 text-center transition',
              dragging
                ? 'border-brand-400/70 bg-brand-500/10'
                : 'border-white/15 bg-white/[0.02] hover:border-white/25',
            ].join(' ')}
          >
            <PhotoIcon className="h-9 w-9 text-white/30" />
            <p className="mt-3 text-sm text-white/80">Drop a folder of photos</p>
            <p className="mt-1 text-xs text-white/45">
              JPEG, PNG, WebP, GIF, AVIF. Read in your browser, never uploaded.
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              className="btn-outline mt-4 text-xs"
            >
              <FolderOpenIcon className="h-4 w-4" />
              Choose photos
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={(e) => {
                loadLocal(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
          </div>

          {/* Google Photos */}
          <div className="flex flex-col items-center rounded-xl border border-white/15 bg-white/[0.02] p-8 text-center">
            <svg className="h-9 w-9" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M11 2v9H2a9 9 0 0 1 9-9Z" />
              <path fill="#FBBC04" d="M22 11h-9V2a9 9 0 0 1 9 9Z" />
              <path fill="#34A853" d="M13 22v-9h9a9 9 0 0 1-9 9Z" />
              <path fill="#4285F4" d="M2 13h9v9a9 9 0 0 1-9-9Z" />
            </svg>
            <p className="mt-3 text-sm text-white/80">Connect Google Photos</p>
            <p className="mt-1 text-xs text-white/45">
              You pick the photos inside Google&rsquo;s own picker. Albums aren&rsquo;t selectable —
              Google removed that API in 2025.
            </p>
            <button
              type="button"
              onClick={connectGoogle}
              disabled={loading || !clientId}
              className="btn mt-4 text-xs"
            >
              Connect
            </button>
            {!clientId && (
              <p className="mt-2 text-[11px] text-amber-300/80">
                Not configured — <code>VITE_GOOGLE_CLIENT_ID</code> is missing.
              </p>
            )}
          </div>
        </div>

        {loading && (
          <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-white/70">{state.progress.label}</p>
              {pickerUri && (
                <a
                  href={pickerUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline shrink-0 text-[11px]"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  Open picker
                </a>
              )}
            </div>
            {state.progress.total > 0 && (
              <>
                <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-brand-500 transition-[width] duration-200"
                    style={{
                      width: `${Math.round((state.progress.loaded / state.progress.total) * 100)}%`,
                    }}
                  />
                </div>
                <p className="text-[11px] text-white/45">
                  {state.progress.loaded} of {state.progress.total}
                </p>
              </>
            )}
            <button type="button" onClick={reset} className="text-[11px] text-white/40 underline">
              Cancel
            </button>
          </div>
        )}

        {state.status === 'error' && (
          <div className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            <p>{state.message}</p>
            {state.hint && <p className="mt-1 text-red-200/70">{state.hint}</p>}
          </div>
        )}

        <p className="text-xs text-white/40">
          Google Photos access is in testing mode, so it only works for accounts I&rsquo;ve added as
          testers. You&rsquo;ll see an &ldquo;unverified app&rdquo; warning — that&rsquo;s expected;
          choose Advanced, then continue.
        </p>
      </div>
    </ExperimentLayout>
  );
};

export default PhotoGalleryExperiment;
